import { FinishedRouteAlert, Message } from "@/reducers/messagesSlice"
import { useCallback } from "react"
import { useAppDispatch } from "@/reducers/hooks"
import { updatePDFViewer } from "@/reducers/overlaysSlice"
import SlickAIGalleryLink from "@/components/chat/SlickAIGalleryLink"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

export function FinishAlertBadgeStats({
  actualDuration,
  expectedDuration,
  actualDistance,
  expectedDistance,
  expectedTolls,
  actualTolls,
  fileName,
  fileUrl,
  message,
}: FinishedRouteAlert & { message: Message }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const onTime = expectedDuration >= actualDuration
  const distanceDifference = actualDistance && actualDistance - expectedDistance
  const durationDifference = actualDuration - expectedDuration

  const expectedCostSum =
    expectedTolls?.reduce((sum, toll) => sum + (toll.price || 0), 0) || 0

  const actualCostSum =
    actualTolls?.reduce((sum, toll) => sum + (toll.price || 0), 0) || 0

  const costDifference = actualCostSum - expectedCostSum

  // Mock fuel data (since it's not in the original data)
  // In production, this would come from the actual data
  const fuelLiters = 148
  // actualDistance
  //   ? Math.round((actualDistance / 1000) * 0.28)
  //   : 0
  // const fuelDifference = distanceDifference
  //   ? Math.round((distanceDifference / 1000) * 0.28)
  //   : 0

  const fuelDifference = -2

  const openPDFViewer = useCallback(() => {
    dispatch(
      updatePDFViewer({
        visible: true,
        PDFMessage: {
          ...message,
          url: fileUrl,
        },
      }),
    )
  }, [fileUrl, dispatch, message])

  let deliveryLink = null

  if (fileUrl && !/\.pdf$/i.test(fileUrl)) {
    deliveryLink = (
      <span>
        <SlickAIGalleryLink href={fileUrl} messageId={message.id}>
          <span className="text-blue-600 underline hover:text-blue-800">
            {fileName || t("maps.finishAlertBadgeStats.file")}
          </span>
        </SlickAIGalleryLink>
      </span>
    )
  } else if (fileUrl && /\.pdf$/i.test(fileUrl)) {
    deliveryLink = (
      <span>
        <a
          href={fileUrl}
          onClick={(e) => {
            e.preventDefault()
            openPDFViewer()
          }}
        >
          <span className="text-blue-600 underline hover:text-blue-800">
            {fileName || t("maps.finishAlertBadgeStats.file")}
          </span>
        </a>
      </span>
    )
  }

  const formatTimeDiff = (seconds: number) => {
    const absSeconds = Math.abs(seconds)
    const hours = Math.floor(absSeconds / 3600)
    const minutes = Math.floor((absSeconds % 3600) / 60)
    const sign = seconds >= 0 ? "+" : "-"
    if (hours === 0) return `${sign}${minutes} min`
    if (minutes === 0) return `${sign}${hours}h`
    return `${sign}${hours}h ${minutes} min`
  }

  return (
    <div className="flex flex-col gap-y-2">
      {deliveryLink && <div>{deliveryLink}</div>}
      <div className="flex gap-2">
        {/* Realizacja card */}
        <div
          className={cn(
            "flex min-w-[140px] flex-col rounded-lg px-4 py-3",
            onTime ? "bg-[#dcfce7]" : "bg-[#fee2e2]",
          )}
        >
          <span className="text-sm font-medium text-[#737373]">
            {t("maps.finishAlertBadgeStats.delivery")}
          </span>
          <span
            className={cn(
              "text-xl font-bold",
              onTime ? "text-[#15803d]" : "text-[#dc2626]",
            )}
          >
            {onTime
              ? t("maps.finishAlertBadgeStats.onTime")
              : t("maps.finishAlertBadgeStats.late")}
          </span>
          <span
            className={cn(
              "text-sm font-medium",
              durationDifference > 0 ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {formatTimeDiff(durationDifference)}
          </span>
        </div>

        {/* Dystans card */}
        <div className="flex min-w-[120px] flex-col rounded-lg bg-[#f5f5f5] px-4 py-3">
          <span className="text-sm font-medium text-[#737373]">
            {t("maps.finishAlertBadgeStats.distance")}
          </span>
          <span className="text-xl font-bold text-[#0a0a0a]">
            {actualDistance ? Math.round(actualDistance / 1000) : 0} km
          </span>
          {distanceDifference !== undefined && distanceDifference !== 0 && (
            <span
              className={cn(
                "text-sm font-medium",
                distanceDifference > 0 ? "text-[#dc2626]" : "text-[#15803d]",
              )}
            >
              {distanceDifference > 0 ? "+" : ""}
              {Math.round(distanceDifference / 1000)} km
            </span>
          )}
        </div>

        {/* Oplaty drogowe card */}
        <div className="flex min-w-[140px] flex-col rounded-lg bg-[#f5f5f5] px-4 py-3">
          <span className="text-sm font-medium text-[#737373]">
            {t("maps.finishAlertBadgeStats.tolls")}
          </span>
          <span className="text-xl font-bold text-[#0a0a0a]">
            {Math.round(actualCostSum)} €
          </span>
          {costDifference !== 0 && (
            <span
              className={cn(
                "text-sm font-medium",
                costDifference > 0 ? "text-[#dc2626]" : "text-[#15803d]",
              )}
            >
              {costDifference > 0 ? "+" : ""}
              {Math.round(costDifference)} €
            </span>
          )}
        </div>

        {/* Paliwo card */}
        <div className="flex min-w-[100px] flex-col rounded-lg bg-[#f5f5f5] px-4 py-3">
          <span className="text-sm font-medium text-[#737373]">
            {t("maps.finishAlertBadgeStats.fuel")}
          </span>
          <span className="text-xl font-bold text-[#0a0a0a]">
            {fuelLiters} l
          </span>
          <span
            className={cn(
              "text-sm font-medium",
              fuelDifference > 0 ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {fuelDifference > 0 ? "+" : ""}
            {fuelDifference} l
          </span>
        </div>
      </div>
    </div>
  )
}
